<template>
  <div class="channel-flow-container">
    <div class="sticky-header">
      <el-steps :active="stepActive" finish-status="success" simple>
        <el-step title="选择项目" />
        <el-step title="选择时间范围" />
        <el-step title="导出数据" />
      </el-steps>

      <div v-if="stepActive === 0" class="search-container">
        <div class="search-box">
          <el-input v-model="searchQuery" placeholder="搜索项目名称或code" @change="handleSearch" clearable>
            <template #append>
              <el-button @click="handleSearch">搜索</el-button>
            </template>
          </el-input>
        </div>
      </div>
    </div>

    <!-- 第一步：选择项目 -->
    <div v-if="stepActive === 0" class="step-panel">

      <el-table :height="projectTableHeight" :data="projectList" @row-click="selectProject" highlight-current-row class="project-table">
        <el-table-column prop="name" label="项目名称" min-width="180" />
        <el-table-column prop="code" label="项目Code" min-width="120" />
        <el-table-column prop="pv" label="昨日PV" min-width="100" />
        <el-table-column prop="uv" label="昨日UV" min-width="100" />
        <el-table-column prop="version" label="埋点版本" min-width="120" />
      </el-table>
    </div>

    <!-- 第二步：选择时间范围和渠道 -->
    <div v-if="stepActive === 1" class="step-panel">
      <div class="time-range-selector">
        <el-date-picker @change="handleSearchChannel()" v-model="timeRange" type="daterange" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" value-format="YYYY-MM-DD" />
      </div>

      <div class="channel-list">
        <h4>选择渠道：</h4>
        <el-checkbox-group v-model="selectedChannels">
          <el-checkbox v-for="channel in channelList" :key="channel" :value="channel"> {{ channel }} </el-checkbox>
        </el-checkbox-group>
      </div>

      <div class="action-buttons">
        <el-button @click="stepActive = 0">上一步</el-button>
        <el-button type="primary" @click="startExport">下一步</el-button>
      </div>
    </div>

    <!-- 第三步：导出结果 -->
    <div v-if="stepActive === 2" class="step-panel">
      <div class="export-result" :style="{ height: projectTableHeight + 'px', overflowY: 'auto' }">
        <div v-for="(message, index) in outputMessages" :key="index" class="message">
          <span v-html="formatMessage(message.text)"></span>
          <div v-if="isUrl(message.text)" class="url-actions">
            <el-button size="small" @click="downloadUrl(message.text)">下载</el-button>
            <el-button size="small" @click="copyUrl(message.text)">复制</el-button>
          </div>
        </div>
      </div>

      <div class="action-buttons">
        <el-button @click="stepActive = 1">上一步</el-button>
        <el-button :loading="reStartLoading" type="primary" @click="stepActive = 0">重新开始</el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { ElMessage } from 'element-plus'
import { fetchEventSource } from '@microsoft/fetch-event-source'
//获取全局
const { appContext } = getCurrentInstance();
const api = appContext.config.globalProperties.$api
// 项目列表数据
const projectList = ref([])
const searchQuery = ref('')
const selectedProject = ref(null)
const timeRange = ref([])
const stepActive = ref(0) // 0:选择项目 1:选择时间 2:导出结果
const outputMessages = ref([])
const channelList = ref([])
const selectedChannels = ref([])
import { useWindowSize } from '@vueuse/core'
const { height } = useWindowSize()
const projectTableHeight = computed(() => height.value - 400);
const reStartLoading = computed(() => isExporting.value) // 绑定按钮 loading
const isExporting = ref(false) // 控制导出中的 loading


// 获取项目列表
async function fetchProjects() {
  try {
    const res = await api.post('/v4/tracking-databoard/api/project/pageQuery', {
      name: searchQuery.value,
      page: 1,
      size: 20
    })
    if (res.success) {
      projectList.value = res.data.list
    }
  } catch (error) {
    ElMessage.error('获取项目列表失败')
  }
}

// 处理搜索
function handleSearch() {
  fetchProjects()
}

async function handleSearchChannel() {
  await fetchChannelList(selectedProject.value.id);
}

// 查询渠道列表
async function fetchChannelList(projectId) {
  try {
    const res = await api.post('/v4/tracking-databoard/api/module/custom/channelList', {
      projectId,
      startTime: timeRange.value?.[0] || '',
      endTime: timeRange.value?.[1] || ''
    })
    if (res.success) {
      channelList.value = res.data.sort()
      selectedChannels.value = [...channelList.value] // 默认全选
      console.log('selectedChannels', selectedChannels.value);
      console.log('channelList', channelList.value);
    }
  } catch (error) {
    ElMessage.error('获取渠道列表失败')
  }
}

// 选择项目
async function selectProject(project) {
  selectedProject.value = project
  stepActive.value = 1 // 跳转到第二步

}

async function startExport() {
  if (!timeRange.value || timeRange.value.length !== 2) {
    ElMessage.warning('请选择完整的时间范围')
    return
  }

  if (selectedChannels.value.length === 0) {
    ElMessage.warning('请至少选择一个渠道')
    return
  }

  isExporting.value = true

  try {
    stepActive.value = 2
    await startSSEConnection()
  } finally {
    isExporting.value = false
  }
}

// 启动SSE连接
async function startSSEConnection() {
  const [startDay, endDay] = timeRange.value
  if (selectedChannels.value.length === 0) {
    ElMessage.warning('请至少选择一个渠道')
    return
  }

  const body = {
    projectCode: selectedProject.value.code,
    startDay,
    endDay,
    version: selectedProject.value.version.toUpperCase(),
    channelList: selectedChannels.value,
    typeList: ["CHANNEL_FLOW_SOURCE"]
  }

  const ctrl = new AbortController()
  await fetchEventSource('https://api.nxqlhhk.top/dw/admin/api/v1/report/run/sse', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: ctrl.signal,
    onopen(response) {
      console.log('SSE连接已建立', response)
    },
    onmessage(msg) {
      if (msg.event === '') {
        outputMessages.value.push({
          text: msg.data,
          type: 'server'
        })
      }
      if (msg.event === 'close') {
        outputMessages.value.push({
          text: '导出完成',
          type: 'system'
        })
        ctrl.abort()
      }
    },
    onerror(err) {
      console.error('SSE错误:', err)
    }
  }
  );

}

// 判断是否是URL
function isUrl(text) {
  try {
    new URL(text)
    return true
  } catch {
    return false
  }
}

// 格式化消息
function formatMessage(text) {
  if (isUrl(text)) {
    return `<a href="${text}" target="_blank">${text}</a>`
  }
  return text
}

// 下载URL
function downloadUrl(url) {
  const a = document.createElement('a')
  a.href = url
  a.download = url.split('/').pop() || 'download'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  ElMessage.success('开始下载')
}

// 复制URL
function copyUrl(url) {
  navigator.clipboard.writeText(url)
    .then(() => ElMessage.success('已复制到剪贴板'))
    .catch(() => ElMessage.error('复制失败'))
}


onMounted(() => {
  init();
})

async function init() {
  await fetchProjects();
}
</script>

<style scoped>
.channel-flow-container {
  width: 100%;
  height: 100%;
  padding: 20px;
  position: relative;
}

.step-header {
  position: sticky;
  top: 0;
  background: white;
  z-index: 100;
  padding: 20px 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.step-panel {
  margin-top: 20px;
  padding: 20px;
  border: 1px solid #eee;
  border-radius: 4px;
}

.search-box {
  width: 600px;
  margin: 0 auto 20px;
}

.search-box .el-input {
  width: 100%;
}

.search-box .el-input__wrapper {
  height: 40px;
  font-size: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.search-box .el-input-group__append {
  background-color: #409eff;
  color: white;
  border: none;
}

.search-box .el-input-group__append:hover {
  background-color: #66b1ff;
}

.channel-list {
  margin: 20px 0;
  max-height: 300px;
  overflow-y: auto;
}

.action-buttons {
  margin-top: 20px;
  text-align: center;
}

.time-range-selector {
  margin-bottom: 20px;
}

/* 输出阶段样式 */
.output-stage {
  height: 100%;
  overflow-y: auto;
}

.sse-output {
  padding: 20px;
}

.message {
  margin-bottom: 10px;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
}

.url-message {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.url-message a {
  color: #409eff;
  text-decoration: none;
}

.url-message a:hover {
  text-decoration: underline;
}

.project-table :deep(.el-table__row) {
  cursor: pointer;
  transition: all 0.3s;
}

.project-table :deep(.el-table__row:hover) {
  background-color: #f5f7fa;
  transform: translateX(5px);
}

.project-table :deep(.el-table__row:active) {
  background-color: #e6f7ff;
}

.sticky-header {
  position: sticky;
  top: 0;
  background: white;
  z-index: 100;
  padding: 20px 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.search-container {
  margin-top: 20px;
}
</style>
